import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AckGroupBody,
  acknowledgeGroup,
  acknowledgeIssue,
  BoardFilters,
  BoardResponse,
  getBoard,
} from '../api/ach/board';
import { useConfig } from './useConfig';

const POLL_MS = 5 * 60 * 1000; // sweep cadence is 15 min; poll while visible.

// useBoard fetches the Campaign Sentinel alerts board for the given filters. Unlike
// the readiness panel (a supplementary column that hides on error), the board IS the
// page, so failures surface as isError for the page to render an error state.
// Polls every 5 min while the tab is focused and refetches on window focus.
export function useBoard(filters: BoardFilters) {
  const { achApiHost } = useConfig();

  return useQuery<BoardResponse>({
    queryKey: ['board', achApiHost, filters],
    queryFn: () => getBoard(achApiHost, filters),
    retry: false,
    keepPreviousData: true,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });
}

// useOpenAlertsCount powers the nav badge: the open-issue count for the user's
// default (My alerts) scope. Degrades silently to 0 on any error — the badge is a
// supplementary hint, never a hard dependency. per_page=1 keeps the payload tiny
// (counts are server-faceted, not page-scoped).
export function useOpenAlertsCount(myId?: number): number {
  const { achApiHost } = useConfig();

  const { data } = useQuery<number>({
    queryKey: ['board-badge', achApiHost, myId],
    queryFn: async () => {
      const board = await getBoard(achApiHost, {
        state: 'open',
        per_page: 1,
        // Same scope the page's My alerts uses: me as trafficker OR account manager.
        ...(myId ? { people_id: [myId] } : {}),
      });
      return board.counts.by_state.open;
    },
    retry: false,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });

  return data ?? 0;
}

// Optimistically flip every issue the predicate matches to `acknowledged` across all
// cached board queries, returning the snapshots so onError can roll back. Counts are
// server-faceted and are corrected by the refetch on settle — we only move rows.
function optimisticallyAck(
  queryClient: ReturnType<typeof useQueryClient>,
  achApiHost: string,
  match: (issue: BoardResponse['contracts'][number]['line_items'][number]['issues'][number]) => boolean
) {
  const key = ['board', achApiHost];
  const snapshots = queryClient.getQueriesData<BoardResponse>({ queryKey: key });
  queryClient.setQueriesData<BoardResponse>({ queryKey: key }, (old) => {
    if (!old) {
      return old;
    }
    return {
      ...old,
      contracts: old.contracts.map((contract) => ({
        ...contract,
        line_items: contract.line_items.map((li) => ({
          ...li,
          issues: li.issues.map((issue) =>
            match(issue) && issue.state === 'open' ? { ...issue, state: 'acknowledged' as const } : issue
          ),
        })),
      })),
    };
  });
  return snapshots;
}

function rollback(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshots: [readonly unknown[], BoardResponse | undefined][]
) {
  snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
}

// useAcknowledgeIssue acks a single issue with an optimistic state flip + refetch.
export function useAcknowledgeIssue() {
  const { achApiHost } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (issueId: number) => acknowledgeIssue(achApiHost, issueId),
    onMutate: async (issueId: number) => {
      await queryClient.cancelQueries({ queryKey: ['board', achApiHost] });
      const snapshots = optimisticallyAck(queryClient, achApiHost, (issue) => issue.id === issueId);
      return { snapshots };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshots) {
        rollback(queryClient, context.snapshots);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['board', achApiHost] }),
  });
}

// useAcknowledgeGroup bulk-acks every open issue sharing a rule within a scope
// (rule + contract) with the same optimistic-then-refetch flow.
export function useAcknowledgeGroup() {
  const { achApiHost } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AckGroupBody) => acknowledgeGroup(achApiHost, body),
    onMutate: async (body: AckGroupBody) => {
      await queryClient.cancelQueries({ queryKey: ['board', achApiHost] });
      const snapshots = optimisticallyAck(
        queryClient,
        achApiHost,
        (issue) => issue.rule_id === body.rule_id
      );
      return { snapshots };
    },
    onError: (_err, _body, context) => {
      if (context?.snapshots) {
        rollback(queryClient, context.snapshots);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['board', achApiHost] }),
  });
}
